/**
 * Перенос `lib/src/utils/exceptions/app_failure.dart`.
 *
 * Dart-перечисления с полем `code` перенесены парой «строковый союз +
 * таблица кодов»: имена участников сохранены, `op.name` и `kind.name` —
 * это само значение союза, а `op.code` и `kind.code` берутся из таблиц
 * [FAILURE_OP_CODE] и [FAILURE_KIND_CODE].
 */

import {
  AppException,
  isHttpError,
  type AppExceptionType,
} from '@/utils/exceptions/appException';
import type { SseFailure, SseFailureKind } from '@/utils/sse/sseFailure';

/**
 * Какую операцию выполняли, когда сломалось.
 *
 * Первая половина кода ошибки: по ней инженер понимает, куда смотреть.
 */
export const FailureOp = {
  config: 'config',
  tablets: 'tablets',
  tabletAuth: 'tabletAuth',
  pin: 'pin',
  questions: 'questions',
  transcribe: 'transcribe',
  completion: 'completion',
  personAnimation: 'personAnimation',
  personChange: 'personChange',
  personMood: 'personMood',
  // Скачивание APK из веб-порта убрано, но операция осталась: на неё
  // ссылаются справочник подсказок и экран диагностики.
  update: 'update',
  record: 'record',
  playback: 'playback',
  permission: 'permission',
} as const;

export type FailureOp = (typeof FailureOp)[keyof typeof FailureOp];

/** `FailureOp.code` — первая половина кода ошибки. */
export const FAILURE_OP_CODE: Record<FailureOp, string> = {
  config: 'CFG',
  tablets: 'TAB',
  tabletAuth: 'AUTH',
  pin: 'PIN',
  questions: 'QST',
  transcribe: 'STT',
  completion: 'LLM',
  personAnimation: 'AVA',
  personChange: 'PRS',
  personMood: 'MOOD',
  update: 'UPD',
  record: 'REC',
  playback: 'PLAY',
  permission: 'PERM',
};

/**
 * Что именно случилось.
 *
 * Вторая половина кода ошибки. Главное отличие от прежнего устройства:
 * 4xx и 5xx разведены — раньше оба давали «Неверный ответ».
 */
export const FailureKind = {
  /** Не дошли до сервера: имя не разрешилось, порт закрыт, сети нет. */
  noNetwork: 'noNetwork',

  /** Не уложились в таймаут установки соединения. */
  connectTimeout: 'connectTimeout',

  /** Не успели отправить тело запроса. */
  sendTimeout: 'sendTimeout',

  /** Сервер принял запрос, но не ответил вовремя. */
  receiveTimeout: 'receiveTimeout',

  /** 5xx — упал бекенд или апстрим за ним. */
  serverDown: 'serverDown',

  /** 4xx — сервер не принял запрос: путь, тело или права. */
  badRequest: 'badRequest',

  /** Ответ пришёл, но не того формата, которого ждём. */
  badContract: 'badContract',

  /** Поток оборвался посреди передачи. */
  streamBroken: 'streamBroken',

  /** Поток открыт, но событий нет дольше допустимого. */
  streamSilent: 'streamSilent',

  /** Запрос отменён приложением. */
  cancelled: 'cancelled',

  /** Сбой на самом устройстве: микрофон, файлы, разрешения. */
  device: 'device',

  /**
   * На сервере не задано то, без чего операция невозможна — например,
   * адрес распознавания речи.
   */
  notConfigured: 'notConfigured',

  /**
   * На самом устройстве нет сети: Wi-Fi выключен или не подключён.
   * Ставится при показе вместо сетевых причин, когда это подтвердил
   * `NetworkStatus` — человеку у киоска нужен Wi-Fi, а не сервер.
   */
  offline: 'offline',

  unknown: 'unknown',
} as const;

export type FailureKind = (typeof FailureKind)[keyof typeof FailureKind];

/** `FailureKind.code` — вторая половина кода ошибки. */
export const FAILURE_KIND_CODE: Record<FailureKind, string> = {
  noNetwork: 'NET',
  connectTimeout: 'CONN',
  sendTimeout: 'SEND',
  receiveTimeout: 'TIMEOUT',
  serverDown: '5XX',
  badRequest: '4XX',
  badContract: 'FORMAT',
  streamBroken: 'BROKEN',
  streamSilent: 'SILENT',
  cancelled: 'CANCEL',
  device: 'DEVICE',
  notConfigured: 'CONFIG',
  offline: 'OFFLINE',
  unknown: 'UNKNOWN',
};

/** `FailureKind.isNetwork` — сбой связи с сервером, а не его ответ. */
export function failureKindIsNetwork(kind: FailureKind): boolean {
  switch (kind) {
    case FailureKind.noNetwork:
    case FailureKind.connectTimeout:
    case FailureKind.sendTimeout:
    case FailureKind.receiveTimeout:
      return true;
    default:
      return false;
  }
}

/** Аргументы конструктора `AppFailure`. */
export interface AppFailureInit {
  readonly op: FailureOp;
  readonly kind: FailureKind;
  readonly statusCode?: number | null;
  readonly serverMessage?: string | null;
  readonly cause?: unknown;
  readonly stackTrace?: string | null;
}

/**
 * Сбой в терминах приложения: что делали, что случилось и что показать.
 *
 * Приходит на смену показу `innerError.toString()` — сырого исключения
 * транспорта с внутренним адресом сервера.
 */
export class AppFailure extends Error {
  /**
   * Поле `cause` заводит сам `Error`: собственное объявление затёрло бы
   * базовое. Значение то же, что в Dart, — исходная ошибка или `null`.
   */
  constructor(init: AppFailureInit) {
    super(undefined, { cause: init.cause ?? null });
    this.name = 'AppFailure';
    this.op = init.op;
    this.kind = init.kind;
    this.statusCode = init.statusCode ?? null;
    this.serverMessage = init.serverMessage ?? null;
    this.stackTrace = init.stackTrace ?? null;
  }

  readonly op: FailureOp;
  readonly kind: FailureKind;

  /** HTTP-код, если сервер успел ответить. */
  readonly statusCode: number | null;

  /**
   * Сообщение, которое прислал сам бекенд. Показывается инженеру в логе;
   * посетителю — только если оно короткое и человеческое (решает каталог).
   */
  readonly serverMessage: string | null;

  readonly stackTrace: string | null;

  /**
   * Как показать человеку: сетевой сбой при отсутствии сети на устройстве —
   * это «включите Wi-Fi», а не «сервер недоступен». Логика повторов и
   * офлайн-выхода по PIN работает с исходным [kind], не с этим.
   */
  presented({ offline }: { offline: boolean }): AppFailure {
    if (!offline || !failureKindIsNetwork(this.kind)) return this;
    return new AppFailure({
      op: this.op,
      kind: FailureKind.offline,
      statusCode: this.statusCode,
      serverMessage: this.serverMessage,
      cause: this.cause,
      stackTrace: this.stackTrace,
    });
  }

  /**
   * Код для справочника: `STT-TIMEOUT`, `LLM-5XX`, `CFG-NET`.
   *
   * Если известен конкретный HTTP-код, он уточняет вторую часть:
   * `PIN-404` информативнее, чем `PIN-4XX`.
   */
  get code(): string {
    const status = this.statusCode;
    const tail =
      status !== null &&
      (this.kind === FailureKind.badRequest || this.kind === FailureKind.serverDown)
        ? `${status}`
        : FAILURE_KIND_CODE[this.kind];
    return `${FAILURE_OP_CODE[this.op]}-${tail}`;
  }

  /** Строка для лога: всё, что известно, без оглядки на пользователя. */
  get diagnostics(): string {
    const parts: string[] = [`${this.code} op=${this.op} kind=${this.kind}`];
    if (this.statusCode !== null) parts.push(`status=${this.statusCode}`);
    if (this.serverMessage !== null) parts.push(`server="${this.serverMessage}"`);
    if (this.cause !== null && this.cause !== undefined) {
      parts.push(`cause=${String(this.cause)}`);
    }
    return parts.join(' · ');
  }

  override toString(): string {
    return this.diagnostics;
  }

  /** Собирает сбой из исключения, пришедшего от HTTP-слоя. */
  static fromException(op: FailureOp, error: unknown, stackTrace?: string | null): AppFailure {
    if (error instanceof AppFailure) return error;

    if (error instanceof AppException) {
      return new AppFailure({
        op,
        kind: kindFromAppException(error),
        statusCode: error.statusCode,
        serverMessage: serverMessageOf(error),
        cause: error.innerError,
        stackTrace: stackTrace ?? error.stackTrace,
      });
    }

    return new AppFailure({
      op,
      kind: FailureKind.unknown,
      cause: error,
      stackTrace,
    });
  }

  /** Собирает сбой из типизированной ошибки SSE-потока. */
  static fromSse(op: FailureOp, failure: SseFailure, stackTrace?: string | null): AppFailure {
    const kind = FAILURE_KIND_BY_SSE_KIND[failure.kind];
    return new AppFailure({
      op,
      kind,
      statusCode: failure.statusCode,
      serverMessage: failure.body,
      cause: failure.cause ?? failure,
      stackTrace,
    });
  }

  /** Сбой на самом устройстве: микрофон, файловая система, разрешения. */
  static device(op: FailureOp, error: unknown, stackTrace?: string | null): AppFailure {
    return new AppFailure({
      op,
      kind: FailureKind.device,
      cause: error,
      stackTrace,
    });
  }
}

/** `switch (failure)` по sealed-классу `SseFailure` — вариант в причину. */
const FAILURE_KIND_BY_SSE_KIND: Record<SseFailureKind, FailureKind> = {
  unreachable: FailureKind.noNetwork,
  connectTimeout: FailureKind.connectTimeout,
  silent: FailureKind.streamSilent,
  badRequest: FailureKind.badRequest,
  serverDown: FailureKind.serverDown,
  broken: FailureKind.streamBroken,
};

/** `body is! Map` — тело ответа читается, только если это объект. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * Текст ошибки, который прислал сервер: `message`/`detail` бекенда или
 * `error.message` Bifrost («virtual key is required.»). Только для лога.
 */
function serverMessageOf(e: AppException): string | null {
  const parsed = e.apiError?.message ?? e.apiError?.detail;
  if (parsed !== null && parsed !== undefined) return parsed;
  const inner = e.innerError;
  const body = asRecord(isHttpError(inner) ? inner.response?.data : null);
  if (body === null) return null;
  const error = body['error'];
  const nested = asRecord(error);
  const candidates: readonly unknown[] = [
    body['message'],
    body['detail'],
    nested !== null ? nested['message'] : error,
  ];
  for (const text of candidates) {
    if (typeof text === 'string' && text.trim().length > 0) return text.trim();
  }
  return null;
}

/**
 * Причина сбоя по причине исключения.
 *
 * Форс-анрапы и жёсткие касты в репозиториях превращают несовпадение
 * контракта в TypeError — для пользователя это «сервер ответил не тем».
 */
const FAILURE_KIND_BY_EXCEPTION_TYPE: Record<AppExceptionType, FailureKind> = {
  connectionTimeout: FailureKind.connectTimeout,
  sendTimeout: FailureKind.sendTimeout,
  receiveTimeout: FailureKind.receiveTimeout,
  connectionError: FailureKind.noNetwork,
  badCertificate: FailureKind.noNetwork,
  cancel: FailureKind.cancelled,
  badResponse: FailureKind.badRequest,
  typeError: FailureKind.badContract,
  stateError: FailureKind.badContract,
  rangeError: FailureKind.badContract,
  formatException: FailureKind.badContract,
  noSuchMethodError: FailureKind.badContract,
  unknown: FailureKind.unknown,
};

/** Код ответа, с которого начинается зона ответственности сервера. */
const SERVER_ERROR_STATUS = 500;

/** Код ответа, с которого начинается зона ответственности запроса. */
const CLIENT_ERROR_STATUS = 400;

function kindFromAppException(e: AppException): FailureKind {
  // Главное расщепление: badResponse раньше накрывал и 400, и 500.
  const status = e.statusCode;
  if (status !== null) {
    if (status >= SERVER_ERROR_STATUS) return FailureKind.serverDown;
    if (status >= CLIENT_ERROR_STATUS) return FailureKind.badRequest;
  }

  return FAILURE_KIND_BY_EXCEPTION_TYPE[e.type];
}
